<template>
  <b-card
    no-body
    class="employee-upcoming-panel mb-0 h-100"
    :class="{ 'employee-upcoming-panel--compact': compact }"
  >
    <b-card-header
      class="d-flex align-items-center justify-content-between border-bottom"
      :class="compact ? 'py-50 px-1' : 'py-75 px-1'"
    >
      <h6 class="mb-0 text-body panel-title">
        {{ hideLeaves ? 'Holidays & WFH' : 'Upcoming' }}
      </h6>
      <div v-if="canCreateHoliday || canCreateCompanyWfh" class="d-flex flex-wrap align-items-center">
        <b-button
          v-if="canCreateHoliday"
          size="sm"
          variant="outline-secondary"
          class="panel-action-btn mr-50"
          @click="$emit('create-holiday')"
        >
          <feather-icon icon="PlusIcon" size="12" class="mr-25" />
          Holiday
        </b-button>
        <b-button
          v-if="canCreateCompanyWfh"
          size="sm"
          variant="outline-secondary"
          class="panel-action-btn"
          @click="$emit('create-company-wfh-day')"
        >
          <feather-icon icon="PlusIcon" size="12" class="mr-25" />
          WFH
        </b-button>
      </div>
    </b-card-header>

    <b-card-body class="d-flex flex-column upcoming-body" :class="compact ? 'p-1' : 'p-1'">
      <div class="upcoming-sections">
        <!-- Holidays -->
        <div class="upcoming-section">
          <div class="d-flex align-items-center section-label mb-50">
            <b-badge
              :variant="compact ? 'light-secondary' : 'danger'"
              class="section-dot-badge mr-50"
              pill
            >&nbsp;</b-badge>
            <span>Holidays</span>
            <b-badge v-if="holidays.length" pill variant="light-secondary" class="ml-auto">
              {{ holidays.length }}
            </b-badge>
          </div>
          <b-list-group v-if="holidays.length" flush class="upcoming-list">
            <b-list-group-item
              v-for="(holiday, index) in visibleHolidays"
              :key="holiday.id || `holiday-${index}`"
              class="d-flex align-items-center justify-content-between upcoming-row holiday-row px-50"
              :class="compact ? 'py-25' : 'py-50'"
            >
              <div class="upcoming-row-main min-w-0 flex-grow-1" :class="{ 'd-flex align-items-center': compact }">
                <span class="upcoming-name text-truncate">
                  {{ holiday.holiday_name || holiday.name || holiday.holiday || 'Holiday' }}
                </span>
                <span class="upcoming-date" :class="{ 'ml-50': compact }">
                  {{ formatDate(holiday.holiday_date || holiday.date) }}
                </span>
              </div>
              <b-badge
                v-if="daysUntil(holiday.holiday_date || holiday.date) != null"
                variant="light-primary"
                class="flex-shrink-0 ml-50"
              >
                {{ daysUntilLabel(holiday.holiday_date || holiday.date) }}
              </b-badge>
            </b-list-group-item>
            <b-button
              v-if="holidays.length > holidayLimit"
              variant="link"
              size="sm"
              class="align-self-start px-0 py-25 text-primary show-more-btn"
              @click="showAllHolidays = !showAllHolidays"
            >
              {{ showAllHolidays ? 'Show less' : `+${holidays.length - holidayLimit} more` }}
            </b-button>
          </b-list-group>
          <div v-else class="upcoming-empty text-muted">No upcoming holidays</div>
        </div>

        <!-- Company WFH -->
        <div class="upcoming-section">
          <div class="d-flex align-items-center section-label mb-50">
            <b-badge
              :variant="compact ? 'light-primary' : 'info'"
              class="section-dot-badge mr-50"
              pill
            >&nbsp;</b-badge>
            <span>Company WFH</span>
            <b-badge v-if="companyWfhDays.length" pill variant="light-secondary" class="ml-auto">
              {{ companyWfhDays.length }}
            </b-badge>
          </div>
          <b-list-group v-if="companyWfhDays.length" flush class="upcoming-list">
            <b-list-group-item
              v-for="(day, index) in visibleWfhDays"
              :key="day.id || `wfh-${index}`"
              class="d-flex align-items-center justify-content-between upcoming-row wfh-row px-50"
              :class="compact ? 'py-25' : 'py-50'"
            >
              <div class="upcoming-row-main min-w-0 flex-grow-1" :class="{ 'd-flex align-items-center': compact }">
                <span class="upcoming-name text-truncate">{{ day.title || 'Company-Wide WFH Day' }}</span>
                <span class="upcoming-date" :class="{ 'ml-50': compact }">{{ formatDate(day.date) }}</span>
              </div>
              <b-badge
                v-if="daysUntil(day.date) != null"
                variant="light-primary"
                class="flex-shrink-0 ml-50"
              >
                {{ daysUntilLabel(day.date) }}
              </b-badge>
            </b-list-group-item>
            <b-button
              v-if="companyWfhDays.length > wfhLimit"
              variant="link"
              size="sm"
              class="align-self-start px-0 py-25 text-primary show-more-btn"
              @click="showAllWfh = !showAllWfh"
            >
              {{ showAllWfh ? 'Show less' : `+${companyWfhDays.length - wfhLimit} more` }}
            </b-button>
          </b-list-group>
          <div v-else class="upcoming-empty text-muted">No upcoming company WFH days</div>
        </div>

        <!-- Leaves (My Leaves) — hidden in Team View -->
        <div v-if="!hideLeaves" class="upcoming-section leaves-section">
          <div class="d-flex align-items-center section-label mb-50">
            <b-badge variant="warning" class="section-dot-badge mr-50" pill>&nbsp;</b-badge>
            <span>{{ leavesLabel }}</span>
            <b-badge v-if="leaves.length" pill variant="light-secondary" class="ml-auto">
              {{ leaves.length }}
            </b-badge>
          </div>
          <div v-if="leavesLoading" class="upcoming-empty text-muted">Loading…</div>
          <b-list-group v-else-if="leaves.length" flush class="upcoming-list">
            <b-list-group-item
              v-for="leave in visibleLeaves"
              :key="leave.id"
              class="d-flex align-items-center justify-content-between upcoming-row leave-row px-50 py-50"
              button
              @click="$emit('leave-click', leave)"
            >
              <div class="upcoming-row-main min-w-0 flex-grow-1">
                <template v-if="isTeamMode">
                  <span class="upcoming-name text-truncate d-block">{{ leaveEmployeeName(leave) }}</span>
                  <span class="upcoming-date">
                    {{ formatLeaveType(leave.leave_type) }} ·
                    {{ formatDate(leave.from_date) }}
                    <template v-if="leave.from_date !== leave.to_date">
                      – {{ formatDate(leave.to_date) }}
                    </template>
                    <span class="leave-days">({{ leave.total_days }}d)</span>
                  </span>
                </template>
                <template v-else>
                  <span class="upcoming-name text-truncate d-block">{{ formatLeaveType(leave.leave_type) }}</span>
                  <span class="upcoming-date">
                    {{ formatDate(leave.from_date) }}
                    <template v-if="leave.from_date !== leave.to_date">
                      – {{ formatDate(leave.to_date) }}
                    </template>
                    <span class="leave-days">({{ leave.total_days }}d)</span>
                  </span>
                </template>
              </div>
              <b-badge variant="light-primary" class="flex-shrink-0 ml-50 leave-status">
                {{ formatApprovalStatus(leave.status) }}
              </b-badge>
            </b-list-group-item>
            <b-button
              v-if="leaves.length > leaveLimit"
              variant="link"
              size="sm"
              class="align-self-start px-0 py-25 text-primary show-more-btn"
              @click="showAllLeaves = !showAllLeaves"
            >
              {{ showAllLeaves ? 'Show less' : `+${leaves.length - leaveLimit} more` }}
            </b-button>
          </b-list-group>
          <div v-else class="upcoming-empty upcoming-empty-compact text-muted">
            {{ isTeamMode ? 'No upcoming team leaves' : 'No upcoming leaves' }}
          </div>
        </div>
      </div>
    </b-card-body>
  </b-card>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BButton,
  BBadge,
  BListGroup,
  BListGroupItem,
} from 'bootstrap-vue'
import moment from 'moment'

export default {
  name: 'EmployeeUpcomingPanel',
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BButton,
    BBadge,
    BListGroup,
    BListGroupItem,
  },
  props: {
    mode: { type: String, default: 'employee' }, // 'employee' | 'team'
    leavesLabel: { type: String, default: 'My Leaves' },
    holidays: { type: Array, default: () => [] },
    companyWfhDays: { type: Array, default: () => [] },
    leaves: { type: Array, default: () => [] },
    leavesLoading: { type: Boolean, default: false },
    canCreateHoliday: { type: Boolean, default: false },
    canCreateCompanyWfh: { type: Boolean, default: false },
    formatDate: { type: Function, required: true },
    formatLeaveType: { type: Function, default: (v) => v || 'Leave' },
    formatApprovalStatus: { type: Function, default: (v) => v || '' },
    getUserName: { type: Function, default: null },
    holidayLimit: { type: Number, default: 3 },
    wfhLimit: { type: Number, default: 2 },
    leaveLimit: { type: Number, default: 3 },
    hideLeaves: { type: Boolean, default: false },
    compact: { type: Boolean, default: false },
  },
  data() {
    return {
      showAllHolidays: false,
      showAllWfh: false,
      showAllLeaves: false,
    }
  },
  computed: {
    isTeamMode() {
      return this.mode === 'team'
    },
    visibleHolidays() {
      return this.showAllHolidays
        ? this.holidays
        : this.holidays.slice(0, this.holidayLimit)
    },
    visibleWfhDays() {
      return this.showAllWfh
        ? this.companyWfhDays
        : this.companyWfhDays.slice(0, this.wfhLimit)
    },
    visibleLeaves() {
      return this.showAllLeaves
        ? this.leaves
        : this.leaves.slice(0, this.leaveLimit)
    },
  },
  methods: {
    leaveEmployeeName(leave) {
      if (!leave) return 'Employee'
      if (typeof this.getUserName === 'function') {
        return this.getUserName(leave.emp_id) || leave.emp_id || 'Employee'
      }
      return leave.emp_id || 'Employee'
    },
    daysUntil(date) {
      if (!date) return null
      const d = moment(date).startOf('day')
      const today = moment().startOf('day')
      if (!d.isValid()) return null
      return d.diff(today, 'days')
    },
    daysUntilLabel(date) {
      const days = this.daysUntil(date)
      if (days == null) return ''
      if (days === 0) return 'Today'
      if (days === 1) return 'Tomorrow'
      return `In ${days}d`
    },
  },
}
</script>

<style lang="scss" scoped>
.employee-upcoming-panel {
  background: #ffffff;
  border: 1px solid #e8ecf0;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  border-radius: 10px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
  max-width: 100%;
  width: 100%;
  box-sizing: border-box;
}

.employee-upcoming-panel--compact {
  box-shadow: none;
}

.employee-upcoming-panel :deep(.card-header) {
  background: #fff;
  flex-shrink: 0;
}

.employee-upcoming-panel :deep(.card-header) .panel-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
}

.employee-upcoming-panel :deep(.card-body.upcoming-body) {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
}

.panel-action-btn {
  font-size: 0.65rem;
  padding: 0.15rem 0.4rem;
  line-height: 1.2;
}

.upcoming-sections {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-right: 0.15rem;
}

.upcoming-sections::-webkit-scrollbar {
  width: 4px;
}

.upcoming-sections::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}

.section-label {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6b7280;
}

.section-dot-badge {
  width: 8px;
  height: 8px;
  min-width: 8px;
  padding: 0 !important;
  line-height: 8px;
}

.upcoming-list :deep(.list-group-item) {
  background: #fafbfc;
  border: 1px solid #f0f2f5 !important;
  border-radius: 6px !important;
  margin-bottom: 0.3rem;
}

.holiday-row {
  border-left: 3px solid #e85d5d !important;
}

.wfh-row {
  border-left: 3px solid #0284c7 !important;
}

.leave-row {
  border-left: 3px solid #f59e0b !important;
}

/* Team compact: no left accent bars */
.employee-upcoming-panel--compact .holiday-row,
.employee-upcoming-panel--compact .wfh-row,
.employee-upcoming-panel--compact .leave-row {
  border-left: 1px solid #f0f2f5 !important;
}

.upcoming-name {
  font-size: 0.75rem;
  font-weight: 600;
  color: #1f2937;
}

.upcoming-date {
  font-size: 0.65rem;
  color: #6b7280;
}

.leave-days {
  color: #0099AC;
  font-weight: 600;
  margin-left: 0.15rem;
}

.leave-status {
  max-width: 72px;
  overflow: hidden;
  text-overflow: ellipsis;
  text-transform: capitalize;
}

.upcoming-empty {
  font-size: 0.7rem;
  padding: 0.1rem 0;
  line-height: 1.25;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.upcoming-empty-compact {
  padding: 0.05rem 0;
}

.show-more-btn {
  font-size: 0.65rem;
  font-weight: 600;
}

.min-w-0 {
  min-width: 0;
}

/* Team View holidays strip — denser than employee Upcoming */
.employee-upcoming-panel--compact :deep(.card-header) .panel-title {
  font-size: 0.8rem;
}

.employee-upcoming-panel--compact .panel-action-btn {
  font-size: 0.6rem;
  padding: 0.1rem 0.3rem;
}

.employee-upcoming-panel--compact .upcoming-sections {
  gap: 0.35rem;
  /* Scroll only when content exceeds card max-height (raised in team-view CSS) */
  overflow-y: auto;
}

.employee-upcoming-panel--compact .section-label {
  font-size: 0.58rem;
  margin-bottom: 0.15rem !important;
}

.employee-upcoming-panel--compact .upcoming-list :deep(.list-group-item) {
  margin-bottom: 0.15rem;
  border-radius: 5px !important;
}

.employee-upcoming-panel--compact .upcoming-name {
  font-size: 0.7rem;
}

.employee-upcoming-panel--compact .upcoming-date {
  font-size: 0.62rem;
  white-space: nowrap;
}

.employee-upcoming-panel--compact .upcoming-empty {
  font-size: 0.65rem;
  padding: 0;
}

.employee-upcoming-panel--compact .show-more-btn {
  font-size: 0.6rem;
}

@media (max-width: 991px) {
  .employee-upcoming-panel {
    height: auto;
    max-height: none;
    overflow: visible;
  }

  .employee-upcoming-panel :deep(.card-body.upcoming-body) {
    overflow: visible;
    height: auto;
  }

  .upcoming-sections {
    max-height: none;
    overflow-y: visible;
  }

  .panel-action-btn {
    min-height: 32px;
    padding: 0.25rem 0.45rem;
  }

  .upcoming-empty {
    white-space: normal;
  }
}

@media (max-width: 575px) {
  .employee-upcoming-panel {
    max-height: none;
  }

  .upcoming-sections {
    max-height: none;
  }
}
</style>
